import { A } from '@adornis/base/env-info.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { ProductLKK } from '@chancenwerk/_cms/products/entities/ProductLKK.js';
import { AcquisitionRecord, AcquisitionRecordDuplicates } from '@chancenwerk/db/AcquisitionRecord.js';
import { Adresse } from '@chancenwerk/db/adresse.js';
import { DuplicateDataTracker } from '@chancenwerk/db/DuplicateDataTracker.js';
import { Registermethode, StudentType } from '@chancenwerk/db/enums/enums.js';
import { Role } from '@chancenwerk/db/semantics/Role.js';
import { SemanticRelationship } from '@chancenwerk/db/semantics/SemanticRelationship.js';
import { StudentParticipationRelationship } from '@chancenwerk/db/semantics/StudentParticipationRelationship.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import type { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import type { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import {
  EntryInformation,
  SchoolInformation,
  Student,
  StudentTags,
} from '@chancenwerk/master-data-management/users/entities/Student.js';
import { KPFPublicInformationAPI } from '@chancenwerk/public-access/api/KPFPublicInformationAPI.js';
import { PartnerPublicInformationAPI } from '@chancenwerk/public-access/api/PartnerPublicInformationAPI.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import { DateTime } from 'luxon';
import { toCanvas } from 'qrcode';
import { from, of, switchMap, tap } from 'rxjs';
import ImageBergBelow from '../assets/logo_chancenreise_untereinander.svg';
import { AnmeldungPageRegistrationBase } from '../components/anmeldung-component-formular-base.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';

/**
 * The visual states that the page can enter.
 */
enum ACQUISITON_PAGE_STATES {
  /** Shows the registration form */
  'Register',
  /** Shows registration was successful */
  'Finished',
  /** Shows rresentation QR-Code mode */
  'Presentation',
  /** Shows that the event is already expired */
  'Expired',
}

/**
 * Development-only switch to force a certain state to show up.
 */
const DEBUG_FORCE_STATE: ACQUISITON_PAGE_STATES | null = null;

const kpfSelector: BaseQLSelectionSet<KPF> = {
  _class: 1,
  _id: 1,
  endDatum: 1,
  email: 1,
  produkt: {
    // @ts-ignore Only exists on a special product
    durchfuehrungsTage: {
      _class: 1,
      wochentag: 1,
      startZeit: 1,
      endZeit: 1,
      locations: 1,
      cid: 1,
      info: 1,
    },
    kuerzel: 1,
    _class: 1,
  },
  schoolID: 1,
  school: {
    _id: 1,
    _class: 1,
    name: 1,
    kuerzel: 1,
    address: Adresse.allFields,
  },
  standardMembershipFee: 1,
};

/**
 * @customElement anmeldung-page-acquisition
 */
@customElement('anmeldung-page-acquisition')
export class AnmeldungPageAcquisition extends AnmeldungPageRegistrationBase {
  @state() private readonly _kpfs = new RXController(
    this,
    from(KPFPublicInformationAPI.getAll()(kpfSelector)).pipe(
      switchMap(async kpfs => {
        kpfs = kpfs.filter(kpf => {
          if (kpf.produkt?._class !== ProductLKK._class) return false;
          if (!kpf.endDatum) return true;
          return kpf.endDatum.toMillis() >= DateTime.now().toMillis();
        });

        const partners = await PartnerPublicInformationAPI.getAll()({
          name: 1,
          address: Adresse.allFields,
        });

        for (const kpf of kpfs) {
          const partner = partners.find(p => p?._id === kpf.schoolID);
          if (!partner) {
            continue;
          }

          kpf.school = constructValue(partner as Partner);
        }

        return kpfs;
      }),
    ),
  );

  @state() private readonly _size = new WindowSizeController(this);
  @state() private readonly _id = new RXController(this, '');
  @state() private readonly _present = new RXController(this, routingState.searchParams.get('present'));
  @state() private readonly _name = new RXController(this, routingState.searchParams.get('name'));
  @state() private readonly _finished = new RXController(this, false);
  @state() private readonly _record = new RXController(
    this,
    this._id.observable.pipe(
      switchMap(kpfId => {
        if (!kpfId) {
          return of(new AcquisitionRecord({}));
        }
        return from(AcquisitionRecord.getAcquisitionRecordForKPF(kpfId)({ _id: 1 })).pipe(
          switchMap(rec =>
            subscribeByID(
              AcquisitionRecord,
              rec?._id ?? '',
            )({
              kpfID: 1,
              createdStudents: 1,
              kpf: {},
              duplicates: {
                probabilityOfDuplicate: 1,
                entityID: 1,
                duplicateID: 1,
                duplicateCollection: 1,
                duplicate: {
                  ...ChancenwerkUser.toStringSelectionSet,
                  kpfs: {
                    produkt: {
                      kuerzel: 1,
                      name: 1,
                    },
                    schoolID: 1,
                    school: {
                      name: 1,
                      kuerzel: 1,
                    },
                  },
                },
              },
            }),
          ),
        );
      }),
    ),
  );
  @state() private readonly _student = new RXController(
    this,
    new Student({
      username: 'new',
      schuelertyp: StudentType.OS,
      guestAccount: true,
      schoolInformation: new SchoolInformation({
        jahrgangsstufe: A.isDevelopment ? 10 : null,
      }),
      joinInformation: new EntryInformation({
        registermethode: Registermethode.OS,
      }),

      notifyKickOff: true,

      ...(A.isDevelopment
        ? {
            vorname: Math.random().toString(36).substring(7),
            nachname: Math.random().toString(36).substring(7),
            email: `${Math.random().toString(36).substring(7)}@adornis.de`,
            handynummer: '+49235678978913',
          }
        : {}),
    }),
  );
  @state() private readonly _form = new FormController(this, this._student.observable, {
    onSuccess: async () => {
      const student = this._student.value;

      if (!student.schoolInformation.jahrgangsstufe) {
        const errMsg = 'Eine Jahrgangsstufe muss ausgewählt sein!';
        throw new ValidationError(errMsg, {
          path: ['schoolInformation'],
          key: 'jahrgangsstufe',
          translationKey: 'errorJahrgangsstufe',
          source: student,
        });
      }

      if (!student.email && !student.handynummer) {
        if (!student.email) {
          throw new ValidationError(`Bitte gebe deine E-Mail an!`, {
            key: 'email',
            source: student,
            translationKey: 'errorEmail',
          });
        }

        if (!student.handynummer) {
          throw new ValidationError(`Bitte gebe deine Handynummer an!`, {
            key: 'handynummer',
            source: student,
            translationKey: 'errorHandynummer',
          });
        }
      }

      await ChromiumDialog.waitFor(
        new Promise<void>(async (resolve, reject) => {
          try {
            this._student.value.addTag(StudentTags.FROM_OS_ACQUISITION);
            const student = await Student.createStudent(this._student.value)({});
            this._student.value._id = student._id;

            if (!this._record.value?.kpfID) {
              throw new Error('KPF nicht gefunden');
            }

            // Build relationship to kpf
            const rel = new StudentParticipationRelationship({
              subjectID: student._id,
              predicateID: Role.STUDENT,
              objectID: this._record.value?.kpfID,
            });
            await SemanticRelationship.saveSemanticRelationshipsAndUpdateRoles([rel])();

            await Student.createDebitorennummer(student)();

            const duplicates = await DuplicateDataTracker.searchForDuplicateOfUserData(this._student.value)({
              entityID: 1,
              duplicateID: 1,
              duplicateCollection: 1,
              probabilityOfDuplicate: 1,
            });

            if (this._record.value && !this._record.value.duplicates) this._record.value.duplicates = [];

            for (const duplicate of duplicates) {
              this._record.value?.duplicates?.push(
                new AcquisitionRecordDuplicates({
                  probabilityOfDuplicate: duplicate.probabilityOfDuplicate,
                  entityID: duplicate.entityID,
                  duplicateID: duplicate.duplicateID,
                  duplicateCollection: duplicate.duplicateCollection,
                }),
              );
            }

            this._record.value?.createdStudents?.push(student._id);
            await this._record.value?.save();
            await Student.sendStudentAcquisitionNotification(this._student.value, this._record.value?.kpfID ?? '')();

            this._finished.next(true);
            resolve();
          } catch (e: any) {
            const errMsg = 'Da ist etwas schiefgegangen.\n' + e.message;

            ChromiumDialog.alert(errMsg);
            return reject(errMsg);
          }
        }),
      );
    },
  });

  canvasRef: Ref<HTMLElement> = createRef();

  override updated(prop: any) {
    super.updated(prop);

    const canvas = this.shadowRoot?.getElementById('canvas');
    if (canvas) {
      toCanvas(
        canvas,
        window.location.origin + window.location.pathname + '?id=' + this._id.value,
        {
          width: 2000,
        },
        error => {
          if (this.canvasRef.value) {
            this.canvasRef.value.style.height = '50vh';
            this.canvasRef.value.style.width = '50vh';
          }
          if (error) console.error(error);
        },
      );
    }
  }

  private _pageState() {
    if (A.isDevelopment && DEBUG_FORCE_STATE) {
      return DEBUG_FORCE_STATE;
    }

    if (this._finished.value) {
      return ACQUISITON_PAGE_STATES.Finished;
    }

    if (this._present.value === '1') {
      return ACQUISITON_PAGE_STATES.Presentation;
    }

    return ACQUISITON_PAGE_STATES.Register;
  }

  private async _submit() {
    await this._form.validate();
  }

  override render() {
    if (this._kpfs.isLoading || !this._kpfs.value) {
      return nothing;
    }

    if (!this._id.value) {
      return html`<cr-flex h100 w100 crossaxis-center padding="xxl" space="xl" ${css({ justifyContent: 'center' })}>
        <img src="${ImageBergBelow}" ${css({ height: '35%' })} />
        <cr-h1>Interesse an der CHANCENreise?</cr-h1>
        <cr-card padding="md">
          <cr-dropdown-search-selection
            ${css({ minWidth: '450px' })}
            .search=${(search: Maybe<string>) => {
              return (this._kpfs.value ?? []).filter(kpf =>
                (kpf.school?.name + ' (' + kpf.school?.address?.toString() + ')')
                  .toLowerCase()
                  .includes(search?.toLowerCase() ?? ''),
              );
            }}
            .renderString=${(kpf: KPF) => kpf.school?.name + ' (' + kpf.school?.address?.toString() + ')'}
            placeholder="Deine Schule"
            @value-picked=${(e: ValueEvent<Maybe<KPF>>) => {
              console.log(e);
              this._id.next(e.detail.value?._id ?? '');
            }}
          ></cr-dropdown-search-selection>
        </cr-card>
      </cr-flex>`;
    }

    switch (this._pageState()) {
      case ACQUISITON_PAGE_STATES.Finished:
        return this._finishedState();
      case ACQUISITON_PAGE_STATES.Expired:
        return this._expiredState();
      case ACQUISITON_PAGE_STATES.Presentation:
        return this._presentState();
      case ACQUISITON_PAGE_STATES.Register:
        return this._regState();
    }

    throw new Error('unknown state');
  }

  private _presentState() {
    return html`<cr-flex h100 w100 crossaxis-center padding="xxl" space="xl" ${css({ justifyContent: 'center' })}>
      <img src="${ImageBergBelow}" ${css({ height: '35%' })} />
      <cr-h1>Interesse an der CHANCENreise?</cr-h1>
      <canvas id="canvas" ${ref(this.canvasRef)}></canvas>
      <cr-h1>${this._name.value}</cr-h1>
    </cr-flex>`;
  }

  private _regState() {
    return html`<cr-section
      ${css({
        height: '100%',
        overflowY: 'auto',
      })}
    >
      <cr-flex h100 w100 space=${this._size.mobile ? 'lg' : 'xl'}>
        <cr-flex crossaxis-center flex h100 space=${this._size.mobile ? 'lg' : 'xl'} ${css({ overflow: 'auto' })}>
          <cr-flex class="headline" crossaxis-start end>
            <cr-flex class="h-content" space="lg" padding="lg">
              <cr-h1> Interesse an der CHANCENreise? </cr-h1>
            </cr-flex>
          </cr-flex>

          <cr-flex class="form-content-page" space="xl" crossaxis-center h100 w100>
            ${this._regStateContent()}
          </cr-flex>
        </cr-flex>
      </cr-flex>
    </cr-section>`;
  }

  private _regStateContent() {
    return html` <cr-flex crossaxis-center h100 w100 ${css({ paddingBottom: '300px' })}>
      <cr-flex crossaxis-center space="sm" ${css({ marginBottom: this.spacing.lg })}>
        <cr-text>
          Du hast Interesse an der CHANCENreise teilzunehmen? Dann benötigen wir folgende Informationen von dir, damit
          wir dich kontaktieren dürfen:
        </cr-text>
      </cr-flex>
      <cr-flex class="section-container" w100 space="lg">
        <cr-image disabled .value=${ImageBergBelow} mode="contain" ${css({ marginTop: 0 })}> </cr-image>

        <cr-flex class="sc-content">
          <cr-flex space="lg" ${css({ minHeight: '300px' })}>
            <cr-flex space="md">
              <cr-flex horizontal space="sm">
                <cr-input placeholder="Vorname *" ${this._form.field('vorname')} ${css({ width: '50%' })}></cr-input>
                <cr-input placeholder="Nachname *" ${this._form.field('nachname')} ${css({ width: '50%' })}></cr-input>
              </cr-flex>
              <cr-flex horizontal space="sm">
                <cr-dropdown-selection
                  ${this._form.field('schoolInformation', 'jahrgangsstufe')}
                  placeholder="Jahrgangsstufe *"
                  .selectables=${ChancenwerkUser.availableGrades.filter(g => g >= 7)}
                  ${css({ width: '50%' })}
                ></cr-dropdown-selection>
                <cr-input
                  placeholder="Klassenzug"
                  ${this._form.field('schoolInformation', 'zug')}
                  ${css({ width: '50%' })}
                ></cr-input>
              </cr-flex>
              <cr-input
                ${css({ width: '100%' })}
                placeholder="E-Mail *"
                type="email"
                ${this._form.field('email')}
              ></cr-input>
              <cr-text ${css({ textAlign: 'center' })}>oder</cr-text>
              <cr-input
                ${css({ width: '100%' })}
                ${this._form.field('handynummer')}
                placeholder="Handynummer *"
                type="tel"
                pattern="[0-9]*"
              ></cr-input>
              <cr-button @click=${() => this._submit()}>Absenden</cr-button>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    </cr-flex>`;
  }

  private _finishedState() {
    return html`
      <cr-section
        ${css({
          height: '100%',
          overflowY: 'auto',
        })}
      >
        <cr-flex h100 w100 space=${this._size.mobile ? 'lg' : 'xl'}>
          <cr-flex crossaxis-center flex h100 space=${this._size.mobile ? 'lg' : 'xl'} ${css({ overflow: 'auto' })}>
            <cr-flex class="headline" crossaxis-start end>
              <cr-flex class="h-content" space="lg" padding="lg">
                <cr-h1> Fast geschafft… </cr-h1>
              </cr-flex>
            </cr-flex>

            <cr-flex class="form-content-page" space="xl" crossaxis-center h100 w100>
              <cr-icon ${css({ fontSize: '3.5rem', color: this.colors.primary })}>check</cr-icon>
              <cr-text bold>Vielen Dank für dein Interesse!</cr-text>
              <cr-text> Wir melden uns zeitnah bei dir! </cr-text>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-section>
    `;
  }

  private _expiredState() {
    return html`<cr-section
      ${css({
        height: '100%',
        overflowY: 'auto',
      })}
    >
      <cr-flex h100 w100 space=${this._size.mobile ? 'lg' : 'xl'}>
        <cr-flex crossaxis-center flex h100 space=${this._size.mobile ? 'lg' : 'xl'} ${css({ overflow: 'auto' })}>
          <cr-flex class="headline" crossaxis-start end>
            <cr-flex class="h-content" space="lg" padding="lg">
              <cr-h1> Abgelaufen! </cr-h1>
            </cr-flex>
          </cr-flex>

          <cr-flex class="form-content-page" space="xl" crossaxis-center h100 w100>
            <cr-icon ${css({ fontSize: '3.5rem', color: this.colors.tone.error })}>warning</cr-icon>
            <cr-text bold>Vielen Dank für dein Interesse!</cr-text>
            <cr-text>Leider ist dieser Link schon abgelaufen.</cr-text>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    </cr-section>`;
  }
}
